import type { EvalPromptProductionMetrics, EvalPromptRanking, EvalPromptTheme } from "@api/rest/types-and-hooks";

export const ProductionSort = {
  Prompt: "prompt",
  Fields: "fields",
  Edited: "edited",
  EditSize: "editSize",
  Rewrite: "rewrite",
  Quality: "quality",
} as const;
export type ProductionSortValue = (typeof ProductionSort)[keyof typeof ProductionSort];

export const SortDirection = { Asc: "asc", Desc: "desc" } as const;
export type SortDirectionValue = (typeof SortDirection)[keyof typeof SortDirection];

/** The direction a column opens in: most concerning rows first. */
export const WORST_FIRST: Record<ProductionSortValue, SortDirectionValue> = {
  [ProductionSort.Prompt]: SortDirection.Asc,
  [ProductionSort.Fields]: SortDirection.Desc,
  [ProductionSort.Edited]: SortDirection.Desc,
  [ProductionSort.EditSize]: SortDirection.Desc,
  [ProductionSort.Rewrite]: SortDirection.Desc,
  [ProductionSort.Quality]: SortDirection.Asc,
};

export interface ProductionRow {
  prompt_key: string;
  /** Null for a global prompt. */
  owner: string | null;
  production: EvalPromptProductionMetrics;
}

export const productionRows = (rankings: EvalPromptRanking[]): ProductionRow[] =>
  rankings.flatMap((r) =>
    r.production ? [{ prompt_key: r.prompt_key, owner: r.owner_organization_name, production: r.production }] : [],
  );

/** Null when the pipeline hasn't sent the value for this row. */
const sortValue = (row: ProductionRow, key: ProductionSortValue): number | string | null => {
  const p = row.production;
  if (key === ProductionSort.Prompt) return row.prompt_key;
  if (key === ProductionSort.Fields) return p.sample_size;
  if (key === ProductionSort.EditSize) return p.avg_edit_percentage;
  if (key === ProductionSort.Rewrite) return p.pct_fields_total_rewrite;
  if (key === ProductionSort.Quality) return p.avg_quality_score;
  return p.pct_fields_edited;
};

/** Sorted copy; missing values go last either way, and ties fall back to
 *  prompt key so the order is stable. */
export const sortProductionRows = (
  rows: ProductionRow[],
  key: ProductionSortValue,
  direction: SortDirectionValue,
): ProductionRow[] => {
  const sign = direction === SortDirection.Asc ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = sortValue(a, key);
    const bv = sortValue(b, key);
    if (av === null || bv === null) {
      return Number(av === null) - Number(bv === null) || a.prompt_key.localeCompare(b.prompt_key);
    }
    const primary = typeof av === "string" ? av.localeCompare(String(bv)) : av - (bv as number);
    return sign * primary || a.prompt_key.localeCompare(b.prompt_key);
  });
};

/** How widely a theme shows: reports within the org when scoped to one,
 *  orgs otherwise — the backend switches what `support` counts. */
export const themeSupportLabel = (theme: EvalPromptTheme, orgScoped: boolean): string =>
  `${theme.support} of ${theme.group_size} ${orgScoped ? "reports" : "orgs"}`;

export interface ProductionSummary {
  prompts: number;
  fields: number;
  /** 0–100, weighted by fields. */
  edited: number;
  /** 0–100, weighted by fields. */
  editSize: number;
  /** 0–1, weighted by fields. */
  quality: number;
}

/** Field-weighted rollup, so a heavily used prompt counts for more than a rare one. */
export const productionSummary = (rows: ProductionRow[]): ProductionSummary | null => {
  const fields = rows.reduce((total, r) => total + r.production.sample_size, 0);
  if (fields === 0) return null;
  const weighted = (pick: (p: EvalPromptProductionMetrics) => number) =>
    rows.reduce((total, r) => total + pick(r.production) * r.production.sample_size, 0) / fields;
  return {
    prompts: rows.length,
    fields,
    edited: weighted((p) => p.pct_fields_edited),
    editSize: weighted((p) => p.avg_edit_percentage),
    quality: weighted((p) => p.avg_quality_score),
  };
};
